import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { TeamNames } from "@/components/teams/names";
import { useTRPC } from "@/integrations/trpc/react";
import {
  type MatchIdProps,
  scoreFromEvents,
  useMatchSets,
  useMatchTeams,
} from "@/lib/matches";
import { invalidateMatchQueries } from "@/lib/tournament-cache";

export type OverrideScoreFormProps = MatchIdProps & {
  tournamentDivisionId?: number;
  setId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function OverrideScoreForm({
  tournamentDivisionId,
  poolMatchId,
  playoffMatchId,
  setId,
  onOpenChange,
  ...props
}: OverrideScoreFormProps) {
  const teams = useMatchTeams({ poolMatchId, playoffMatchId });
  const sets = useMatchSets({ poolMatchId, playoffMatchId });

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.matches.overrideScore.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(
        trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
      );
      queryClient.invalidateQueries(
        trpc.matches.getScoreEvents.queryOptions({
          poolMatchId,
          playoffMatchId,
        }),
      );

      invalidateMatchQueries(queryClient, trpc);

      onOpenChange(false);
    },
    onError: () => {
      // Refetch the viewer so stale permission state is cleared.
      // If the session expired, this hides the TD controls on next render.
      queryClient.invalidateQueries({ queryKey: ["viewer"] });
    },
  });

  const set = sets?.find((s) => s.id === setId);

  // Compute current score from events cache (matches the ScoreBoard display)
  // rather than the potentially stale set record scores.
  const eventsKey = trpc.matches.getScoreEvents.queryOptions({
    poolMatchId,
    playoffMatchId,
  }).queryKey;
  const cachedEvents =
    queryClient.getQueryData<
      Record<number, Parameters<typeof scoreFromEvents>[0]>
    >(eventsKey);
  const setEvents = cachedEvents?.[setId];
  const currentScore = setEvents
    ? scoreFromEvents(setEvents)
    : { a: set?.teamAScore ?? 0, b: set?.teamBScore ?? 0 };

  const winScore = set?.winScore ?? 21;
  const schema = useMemo(
    () =>
      z
        .object({
          teamAScore: z.number(),
          teamBScore: z.number(),
        })
        .refine(
          ({ teamAScore, teamBScore }) => {
            const maxScore = Math.max(teamAScore, teamBScore);
            const minScore = Math.min(teamAScore, teamBScore);

            if (maxScore > winScore && maxScore - minScore > 2) {
              return false;
            }

            return true;
          },
          {
            message: `Invalid score. If either score exceeds ${winScore}, the difference must be 2 or less.`,
            path: ["teamBScore"],
          },
        ),
    [winScore],
  );

  const form = useAppForm({
    defaultValues: {
      teamAScore: currentScore.a,
      teamBScore: currentScore.b,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { teamAScore, teamBScore }, formApi }) => {
      if (!set) return;
      if (teamAScore === currentScore.a && teamBScore === currentScore.b) {
        onOpenChange(false);
        return;
      }
      mutate(
        {
          id: set.id,
          teamAScore,
          teamBScore,
        },
        {
          onSuccess: () => {
            formApi.reset();
          },
        },
      );
    },
  });

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Override Score
        </Heading>

        {set && (
          <p className="text-sm text-gray-600">
            {sets && sets.length > 1
              ? `Set ${set.setNumber} — playing to ${set.winScore}`
              : `Playing to ${set.winScore}`}
            {set.switchScore ? `, switch every ${set.switchScore}` : ""}
          </p>
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to override score"}
                description={
                  failureReason.message === "UNAUTHORIZED"
                    ? "Your session has expired. Please refresh the page and log in again."
                    : failureReason.message
                }
              />
            </form.AppForm>
          )}

          <form.AppField
            name="teamAScore"
            children={(field) => (
              <field.Number
                label={<TeamNames players={teams?.teamA?.team.players || []} />}
                field={field}
              />
            )}
          />

          <form.AppField
            name="teamBScore"
            children={(field) => (
              <field.Number
                label={<TeamNames players={teams?.teamB?.team.players || []} />}
                field={field}
              />
            )}
          />

          <form.Subscribe selector={(state) => state.errors}>
            {(errors) =>
              errors.length > 0 ? (
                <form.AppForm>
                  <form.Alert
                    title="Invalid score"
                    description={errors
                      .flatMap((e) =>
                        typeof e === "string"
                          ? [e]
                          : Array.isArray(e)
                            ? e.map((x) =>
                                typeof x === "object" && x && "message" in x
                                  ? (x as { message: string }).message
                                  : String(x),
                              )
                            : [],
                      )
                      .join(". ")}
                  />
                </form.AppForm>
              ) : null
            }
          </form.Subscribe>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>
                Submit
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
